import React from "react";
import { Box, Typography, Button } from "@mui/material";
import {
  border, textPrimary, textSecondary, textMuted, accent, font, success, warning,
  surfaceRaised, easing,
} from "../../../layouts/schoolPortal/schoolPortalTheme";

/**
 * The work that was set, and how far through it they are.
 *
 * WHY THIS IS ON THE PAGE AT ALL
 * Setting work is the instructor's main move between lessons, and until now
 * the record showed only that some tasks existed, a count on a shut drawer.
 * Whether the student had touched any of them was invisible, which makes the
 * next conversation a guess: you cannot tell "they tried and it did not stick"
 * from "they never opened it", and those are opposite conversations.
 *
 * PROGRESS IS COUNTED, NOT REPORTED
 * `progress` comes from studentTaskProgress, which counts real answers against
 * the task's own target since the moment it was set. Nobody ticks anything.
 * A task with no target cannot be measured and says so rather than showing an
 * empty bar that will never move.
 */

const dueLabel = (d) => {
  if (!d) return null;
  const days = Math.round((new Date(d) - Date.now()) / 864e5);
  if (days < 0) return { text: `${Math.abs(days)}d overdue`, color: warning };
  if (days === 0) return { text: "due today", color: warning };
  if (days === 1) return { text: "due tomorrow", color: textSecondary };
  return { text: `due in ${days}d`, color: textMuted };
};

/** "low-level-wind-shear-approach" -> "Low level wind shear approach" */
const humanise = (slug) => String(slug || "")
  .replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim()
  .replace(/^./, (c) => c.toUpperCase());

/**
 * Where the work sits, as a path rather than a single mashed name.
 *
 * The title only ever names the NARROWEST level, so "Redo Low level wind shear
 * approach" gave no way to tell which subject it belonged to, or whether that
 * string was a topic or a sub-topic. Reading it back as
 * "Meteorology > Wind" under the title makes each level legible as a level.
 */
function scopeOf(target) {
  if (!target?.subject) return null;
  return [target.subject, target.topic]
    .filter(Boolean)
    .map(humanise)
    .join("  ›  ");
}

function TaskRow({ task, count = 1 }) {
  const p = task.progress || null;
  const target = task.target || null;
  const measurable = Boolean(target?.kind && p && p.required);
  const pct = measurable ? Math.min(100, p.percent ?? 0) : null;
  const due = dueLabel(task.dueAt);
  const scope = scopeOf(target);
  const done = measurable && p.met;

  return (
    <Box sx={{ py: 1, borderTop: `1px solid ${border}` }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: measurable ? 0.5 : 0 }}>
        <Typography noWrap sx={{ fontFamily: font, fontSize: "12.5px", color: textPrimary, flex: 1, minWidth: 0 }}>
          {task.title}
        </Typography>

        {count > 1 && (
          // Four identical rows is not four pieces of work. Collapsing them
          // keeps the panel honest about how much is actually outstanding.
          <Typography sx={{
            fontFamily: font, fontSize: "11px", fontWeight: 700, flexShrink: 0,
            color: textMuted, fontVariantNumeric: "tabular-nums",
          }}>
            &times;{count}
          </Typography>
        )}

        {measurable && (
          <Typography sx={{
            fontFamily: font, fontSize: "11.5px", fontWeight: 700, flexShrink: 0,
            fontVariantNumeric: "tabular-nums",
            color: done ? success : task.stalled ? warning : textSecondary,
          }}>
            {p.answered}/{p.required}
            {p.accuracy != null && target.minAccuracy ? ` · ${p.accuracy}%` : ""}
          </Typography>
        )}
      </Box>

      {scope && (
        <Typography noWrap sx={{
          fontFamily: font, fontSize: "10.5px", color: textMuted, mt: 0.15, mb: 0.4,
        }}>
          {scope}
        </Typography>
      )}

      {measurable && (
        <Box sx={{ height: 4, borderRadius: 2, backgroundColor: surfaceRaised, overflow: "hidden", mb: 0.4 }}>
          <Box sx={{
            width: `${Math.max(pct ? 2 : 0, pct)}%`, height: "100%",
            backgroundColor: done ? success : task.stalled ? warning : accent,
            transition: `width 0.3s ${easing}`,
          }} />
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        {/* The one thing worth spotting without opening anything: not "it is
            late", but "they never started". */}
        {task.stalled && (
          <Typography sx={{ fontFamily: font, fontSize: "11px", color: warning, fontWeight: 600 }}>
            not started
          </Typography>
        )}
        {done && (
          <Typography sx={{ fontFamily: font, fontSize: "11px", color: success, fontWeight: 600 }}>
            finished
          </Typography>
        )}
        {due && (
          <Typography sx={{ fontFamily: font, fontSize: "11px", color: due.color }}>
            {due.text}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function RecordWork({ record, onSetWork, studentName }) {
  const tasks = record?.openItems?.tasks || [];
  const awaiting = record?.openItems?.awaitingApproval || [];

  /**
   * Identical work, set more than once, is one row with a count.
   *
   * A student in the live data carries four tasks all called "Start their
   * first subject", left behind by a composer bug that has since been fixed
   * (see RecordGetStarted.jsx). Stacked, they filled the panel with the same
   * sentence four times and made a record with one piece of outstanding work
   * look like a backlog. Grouping is the fix rather than a migration, because
   * an instructor setting the same task twice by accident produces exactly the
   * same mess tomorrow.
   *
   * Keyed on title plus due date: the same title genuinely set for two
   * different weeks IS two pieces of work and must stay two rows.
   */
  const grouped = React.useMemo(() => {
    const out = [];
    const byKey = new Map();
    for (const t of tasks) {
      const key = `${(t.title || "").trim().toLowerCase()}|${t.dueAt || ""}`;
      const seen = byKey.get(key);
      if (seen) { seen.count += 1; continue; }
      const entry = { task: t, count: 1 };
      byKey.set(key, entry);
      out.push(entry);
    }
    return out;
  }, [tasks]);

  const untargeted = grouped.filter(({ task }) => !(task.target?.kind && task.progress?.required)).length;

  return (
    <Box sx={{
      backgroundColor: "#fff", border: `1px solid ${border}`, borderRadius: "10px",
      px: 1.75, py: 1.5, mb: 2,
    }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1.5 }}>
        <Typography sx={{ fontFamily: font, fontSize: "16px", fontWeight: 800, color: textPrimary, letterSpacing: "-0.01em" }}>
          Monitor tasks set
        </Typography>
        <Button
          onClick={onSetWork}
          sx={{
            textTransform: "none", fontFamily: font, fontSize: "12px", fontWeight: 700,
            borderRadius: "999px", px: 1.5, py: 0.35, minHeight: 0,
            color: "#fff", backgroundColor: accent,
            "&:hover": { backgroundColor: "#a81828" },
          }}
        >
          Set a task
        </Button>
      </Box>

      {grouped.map(({ task, count }) => <TaskRow key={task.id} task={task} count={count} />)}

      {/* Said once, at the bottom, instead of "no target set" stamped against
          every line. Repeating it made a property of how the work was SET read
          like a verdict on the student, and when nothing has a target, which
          is currently every task in the system, the panel was more label than
          content. */}
      {untargeted > 0 && (
        <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, mt: 1, pt: 1, borderTop: `1px solid ${border}`, lineHeight: 1.5 }}>
          {untargeted === grouped.length
            ? "None of these have a target, so there is no progress to show. Set a task with a target and it fills in here as they answer."
            : `${untargeted} ${untargeted === 1 ? "task has" : "tasks have"} no target, so ${untargeted === 1 ? "it cannot" : "they cannot"} show progress.`}
        </Typography>
      )}

      {awaiting.length > 0 && (
        <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: warning, mt: 1, pt: 1, borderTop: `1px solid ${border}` }}>
          {awaiting.length} waiting on your approval before {studentName || "the student"} can see {awaiting.length === 1 ? "it" : "them"}.
        </Typography>
      )}
    </Box>
  );
}
